/**
 * SelectedRateDetail — Detailed breakdown panel for a selected rate.
 * Shows rate/price/payment metrics, price breakdown table, compensation info,
 * borrower impact, and program details.
 *
 * Props:
 *   selectedRate        — selected rate object
 *   loanAmount          — effective loan amount
 *   compensationType    — 'LPC' | 'BPC'
 *   activeCompensation  — active comp schedule object (or null)
 *   bpcCalcMethod       — 'PercentOfLoan' | 'FixedAmount'
 *   bpcValue            — string BPC value
 *   lockDuration        — number of lock days
 *   onClose             — callback() to clear selection
 *   children            — optional extra content (e.g. assign/lock buttons) rendered in the right column
 */
import { forwardRef } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { fmt, fmtDollar, fmtPct } from './pricingUtils';

const SelectedRateDetail = forwardRef(({
  selectedRate,
  loanAmount,
  compensationType = 'LPC',
  activeCompensation,
  bpcCalcMethod,
  bpcValue,
  lockDuration,
  onClose,
  children,
}, ref) => {
  if (!selectedRate) return null;

  const isLpc = compensationType === 'LPC';
  const adjPrice = selectedRate.adjustedPrice;
  const priceDelta = adjPrice - 100;
  const priceDeltaDollars = (priceDelta / 100) * loanAmount;

  let compPct = 0;
  let compDollars = 0;
  if (isLpc && activeCompensation) {
    compPct = activeCompensation.percentage || 0;
    compDollars = (compPct / 100) * loanAmount;
    if (activeCompensation.minDollarAmount != null && compDollars < activeCompensation.minDollarAmount)
      compDollars = activeCompensation.minDollarAmount;
    if (activeCompensation.maxDollarAmount != null && compDollars > activeCompensation.maxDollarAmount)
      compDollars = activeCompensation.maxDollarAmount;
  } else if (!isLpc && bpcValue) {
    compDollars = bpcCalcMethod === 'PercentOfLoan'
      ? (loanAmount * (parseFloat(bpcValue) || 0) / 100)
      : (parseFloat(bpcValue) || 0);
    compPct = loanAmount > 0 ? (compDollars / loanAmount) * 100 : 0;
  }

  const borrowerCreditOrCost = priceDeltaDollars;

  return (
    <Card ref={ref} variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2, mt: 2 }}>
      <CardHeader
        sx={{ py: 1.5 }}
        title={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Selected Rate — {selectedRate.productName}</Typography>
            </Stack>
            <Button size="small" color="inherit" onClick={onClose} startIcon={<CloseIcon fontSize="small" />}>
              Close
            </Button>
          </Stack>
        }
        disableTypography
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          {/* Left column: metrics + pricing breakdown */}
          <Grid size={{ xs: 12, lg: 6 }}>
            {/* Headline metrics */}
            <Stack direction="row" justifyContent="space-around" textAlign="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Rate</Typography>
                <Typography variant="h5" fontWeight={700}>{fmtPct(selectedRate.rate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Price</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: adjPrice >= 100 ? 'success.main' : 'error.main' }}>
                  {fmt(adjPrice, 3)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{selectedRate.isIO ? 'Est. IO' : 'Est. P&I'}</Typography>
                <Typography variant="h5" fontWeight={700}>{selectedRate.monthlyPayment ? fmtDollar(selectedRate.monthlyPayment) : '—'}</Typography>
              </Box>
            </Stack>

            {/* Price Breakdown */}
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Price Breakdown</Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small" sx={{ '& td': { fontSize: '0.8rem', py: 0.25 } }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Base Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: selectedRate.basePrice >= 100 ? 'success.main' : 'error.main' }}>
                      {fmt(selectedRate.basePrice, 3)}
                    </TableCell>
                  </TableRow>
                  {selectedRate.adjustments?.map((adj, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ color: 'text.secondary', pl: 3, fontSize: '0.75rem' }}>{adj.description}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: adj.points > 0 ? 'error.main' : adj.points < 0 ? 'success.main' : 'text.primary' }}>
                        {adj.points > 0 ? '-' : adj.points < 0 ? '+' : ''}{fmt(Math.abs(adj.points), 3)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {selectedRate.totalAdjustment !== 0 && (
                    <TableRow sx={{ borderTop: 1, borderColor: 'divider' }}>
                      <TableCell sx={{ color: 'text.secondary', pl: 3, fontWeight: 700, fontSize: '0.75rem' }}>
                        Total Adjustments {isLpc && <Typography component="span" variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>(incl. compensation)</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: selectedRate.totalAdjustment > 0 ? 'error.main' : 'success.main' }}>
                        {selectedRate.totalAdjustment > 0 ? '-' : '+'}{fmt(Math.abs(selectedRate.totalAdjustment), 3)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Net Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: adjPrice >= 100 ? 'success.main' : 'error.main' }}>
                      {fmt(adjPrice, 3)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Compensation */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <WalletIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary">Your Compensation ({compensationType})</Typography>
            </Stack>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">Comp Rate</Typography>
                <Typography variant="body2" fontWeight={700}>{compPct > 0 ? `${fmt(compPct, 3)}%` : '—'}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">You Earn</Typography>
                <Typography variant="body1" fontWeight={700} color="success.main">
                  {compDollars > 0 ? fmtDollar(compDollars) : '—'}
                </Typography>
              </Stack>
              {isLpc && activeCompensation && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                  LPC — deducted from price by lender
                  {activeCompensation.minDollarAmount != null && activeCompensation.maxDollarAmount != null && (
                    <> · Range: ${(activeCompensation.minDollarAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}–${(activeCompensation.maxDollarAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
                  )}
                </Typography>
              )}
              {!isLpc && bpcValue && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                  BPC — paid by borrower at closing (not deducted from price)
                </Typography>
              )}
            </Paper>

            {/* Borrower Impact */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary">Borrower Impact</Typography>
            </Stack>
            <Paper variant="outlined" sx={(theme) => ({ p: 1.5, mb: 2, bgcolor: borrowerCreditOrCost >= 0 ? theme.vars.palette.badges.success.bg : theme.vars.palette.badges.danger.bg })}>
              {borrowerCreditOrCost >= 0 ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} color="success" />
                      <Typography variant="caption">Lender Credit</Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight={700} color="success.main">{fmtDollar(borrowerCreditOrCost)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Price is above the 100.000 par target ({fmt(priceDelta, 3)} pts) — borrower receives a credit toward closing costs
                  </Typography>
                </>
              ) : (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} color="error" />
                      <Typography variant="caption">Discount Points</Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight={700} color="error.main">{fmtDollar(Math.abs(borrowerCreditOrCost))}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Price is below the 100.000 par target ({fmt(priceDelta, 3)} pts) — borrower pays discount points to buy down the rate
                  </Typography>
                </>
              )}
              {!isLpc && bpcValue && compDollars > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'rgba(0,0,0,0.1)' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" fontWeight={700}>Broker Compensation (BPC)</Typography>
                    <Typography variant="caption" fontWeight={700} color="error.main">{fmtDollar(compDollars)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Paid by borrower at closing</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right column: lock/loan details + program details + children */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small" sx={{ '& td': { fontSize: '0.8rem', py: 0.25 } }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Lock Term</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{lockDuration} days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Lock Expires</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {(() => { const exp = new Date(); exp.setDate(exp.getDate() + lockDuration); return exp.toLocaleDateString(); })()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Loan Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtDollar(loanAmount)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Program Details</Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small" sx={{ '& td': { fontSize: '0.8rem', py: 0.25 } }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Family</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{selectedRate.family}</TableCell>
                  </TableRow>
                  {selectedRate.subfamily && (
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Subfamily</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{selectedRate.subfamily}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{selectedRate.typeName}</TableCell>
                  </TableRow>
                  {selectedRate.paymentType && (
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Payment</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{selectedRate.paymentType}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Extra content from parent (assign/lock buttons, etc.) */}
            {children}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

SelectedRateDetail.displayName = 'SelectedRateDetail';

export default SelectedRateDetail;
